"use client"
import { useState, memo } from "react"
import { Badge } from "@/components/ui/badge"
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from "@/components/ui/context-menu"
import {
  Plus,
  Heart,
  Minus,
  ExternalLink,
  Archive,
  Flag,
  Lock,
  AlertCircle,
  Ban,
  ThumbsUp,
  ThumbsDown,
  EyeOff,
  FolderPlus,
  ListPlus,
  Check,
} from "@/lib/icons"
import { useTagFiltersStore } from "@/lib/tag-filters-store"
import { useBlacklistStore } from "@/lib/blacklist-store"
import { useTagSetStore } from "@/lib/tag-set-store"
import { cn } from "@/lib/utils"
import { getTagCategory, getTagColor, getTagColorElevated, getTagTextColor, getTagTextColorElevated } from "@/lib/tag-categorizer"
import { toast as sonnerToast } from "sonner"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { useTranslations } from "next-intl"
import { useFeatureFlag } from "@/lib/feature-flags"

interface TagWithContextMenuProps {
  tag: string
  variant?: "default" | "secondary" | "outline"
  showPlus?: boolean
  showMinus?: boolean
  onClick?: () => void
  onNegativeClick?: () => void
  onRemove?: () => void
  className?: string
  onOpenInNewSet?: (tag: string) => void
  onOpenInBackgroundSet?: (tag: string) => void
  onDispute?: (tag: string, reason: string) => void
  isImportedTag?: boolean
  isApproved?: boolean
  isLocked?: boolean
  disputeCount?: number
  trustPercentage?: number
  postId?: string
  onTrustVote?: (tag: string, isAccurate: boolean) => void
  elevated?: boolean
}

export const TagWithContextMenu = memo(function TagWithContextMenu({
  tag,
  variant = "outline",
  showPlus = false,
  showMinus = false,
  onClick,
  onNegativeClick,
  onRemove,
  className,
  onOpenInNewSet,
  onOpenInBackgroundSet,
  onDispute,
  isImportedTag = false,
  isApproved = false,
  isLocked = false,
  disputeCount = 0,
  trustPercentage = 0,
  postId,
  onTrustVote,
  elevated = false,
}: TagWithContextMenuProps) {
  const t = useTranslations("tagWithContextMenu")
  // Dispute / Accurate / Inaccurate feed community tag suggestions, which are off until user-hosted content ships.
  const { enabled: suggestionsOn } = useFeatureFlag("tag_suggestions")
  const { addFilter, removeFilter, getFilterType } = useTagFiltersStore()
  // The app mounts sonner's Toaster only; the older useToast hook has no Toaster, so toasts sent through it were never shown (this menu's
  // "new search opened", "tag disputed" and the new "tag hidden" ones included).
  const toast = ({ title, description, duration }: { title: string; description?: string; duration?: number }) =>
    sonnerToast(title, { description, duration })
  const [showDisputeDialog, setShowDisputeDialog] = useState(false)
  const [disputeReason, setDisputeReason] = useState("")
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false)
  const [newGroupName, setNewGroupName] = useState("")

  // Hiding a tag (it used to be "hate"): the blacklist, as a single tag or inside one of the viewer's own groups.
  const cleanTag = tag.trim().toLowerCase()
  const blacklistedTags = useBlacklistStore((s) => s.blacklistedTags)
  const blacklistGroups = useBlacklistStore((s) => s.groups)
  const addBlacklistTag = useBlacklistStore((s) => s.addTag)
  const removeBlacklistTag = useBlacklistStore((s) => s.removeTag)
  const addTagToGroup = useBlacklistStore((s) => s.addTagToGroup)
  const removeTagFromGroup = useBlacklistStore((s) => s.removeTagFromGroup)
  const createBlacklistGroup = useBlacklistStore((s) => s.createGroup)
  const hiddenAlone = blacklistedTags.includes(cleanTag)
  const isHidden = hiddenAlone || blacklistGroups.some((g) => g.enabled && g.tags.includes(cleanTag))

  // Sets (saved searches): add this tag to one that already exists.
  const tagSets = useTagSetStore((s) => s.sets)
  const updateTagSet = useTagSetStore((s) => s.updateSet)

  const displayTag = tag.replace(/_/g, " ").replace("artist:", "").replace("_(artist)", "")
  const filterType = getFilterType(tag)
  const category = getTagCategory(tag)
  const categoryColor = elevated ? getTagColorElevated(category) : getTagColor(category)
  const categoryTextColor = elevated ? getTagTextColorElevated(category) : getTagTextColor(category)

  const handleLoveTag = () => {
    if (filterType === "love") {
      removeFilter(tag)
    } else {
      addFilter(tag, "love")
    }
  }

  const toggleHiddenAlone = () => {
    if (hiddenAlone) removeBlacklistTag(cleanTag)
    else {
      addBlacklistTag(cleanTag)
      toast({ title: t("tagHidden"), description: t("tagHiddenDescription", { name: displayTag }), duration: 2000 })
    }
  }

  const toggleInGroup = (groupId: string, inGroup: boolean, groupName: string) => {
    if (inGroup) removeTagFromGroup(groupId, cleanTag)
    else {
      addTagToGroup(groupId, cleanTag)
      toast({ title: t("tagHidden"), description: t("tagAddedToGroup", { name: displayTag, group: groupName }), duration: 2000 })
    }
  }

  const handleCreateGroup = () => {
    const name = newGroupName.trim()
    if (!name) return
    createBlacklistGroup(name, [cleanTag])
    setShowNewGroupDialog(false)
    setNewGroupName("")
    toast({ title: t("tagHidden"), description: t("tagAddedToGroup", { name: displayTag, group: name }), duration: 2000 })
  }

  const addToSet = (setId: string, setName: string, setTags: string[], setNegativeTags: string[]) => {
    if (setTags.includes(tag)) return
    updateTagSet(setId, [...setTags, tag], setNegativeTags)
    toast({ title: t("addedToSet"), description: t("addedToSetDescription", { name: displayTag, set: setName }), duration: 2000 })
  }

  const handleOpenInNewSetCallback = () => {
    if (!onOpenInNewSet) return
    onOpenInNewSet(tag)
    toast({
      title: t("newSearchOpened"),
      description: t("newSearchOpenedDescription", { name: displayTag }),
      duration: 2000,
    })
  }

  const handleOpenInBackgroundSetCallback = () => {
    if (!onOpenInBackgroundSet) return
    onOpenInBackgroundSet(tag)
    toast({
      title: t("backgroundSearchSaved"),
      description: t("backgroundSearchSavedDescription", { name: displayTag }),
      duration: 2000,
    })
  }

  const handleDispute = () => {
    if (disputeReason.trim()) {
      onDispute?.(tag, disputeReason)
      setShowDisputeDialog(false)
      setDisputeReason("")
      toast({
        title: t("tagDisputed"),
        description: t("disputeFiled", { name: displayTag }),
        duration: 2000,
      })
    }
  }

  const handleTrustVote = async (isAccurate: boolean) => {
    if (!postId) return

    try {
      const response = await fetch("/api/trust-vote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postId, tag, isAccurate }),
      })

      if (response.ok) {
        onTrustVote?.(tag, isAccurate)
        toast({
          title: isAccurate ? t("markedAsAccurate") : t("markedAsInaccurate"),
          description: t("voteRecorded", { name: displayTag }),
          duration: 2000,
        })
      }
    } catch (error) {
      console.error("[v0] Error voting on tag accuracy:", error)
    }
  }

  const textColorStyle = {
    color: trustPercentage > 0
      ? `rgb(${255 - trustPercentage * 0.45}, ${255 - trustPercentage * 0.05}, ${255 - trustPercentage * 2.55})`
      : undefined,
  }

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className="inline-flex items-center gap-0.5 group">
            <Badge
              variant={variant}
              className={cn(
                "cursor-pointer hover:bg-accent/50 [@media(pointer:coarse)]:bg-accent/30 transition-colors select-none relative",
                filterType === "love" &&
                  "border-2 border-rose-400 dark:border-rose-500 text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/60 hover:bg-rose-200 dark:hover:bg-rose-900/80",
                filterType !== "love" && isHidden &&
                  "border-2 border-gray-400 dark:border-gray-500 text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700",
                filterType !== "love" && !isHidden && !isApproved && categoryColor,
                filterType !== "love" && !isHidden && !isApproved && categoryTextColor,
                filterType !== "love" && !isHidden &&
                  isApproved &&
                  "bg-amber-500/20 hover:bg-amber-500/30 border-amber-500/40 text-amber-700 dark:text-amber-400",
                className,
              )}
              style={filterType !== "love" && !isHidden && !isApproved ? textColorStyle : undefined}
              onClick={onClick}
            >
              {showPlus ? (
                <Plus className="h-3 w-3 mr-1 opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 transition-opacity" />
              ) : null}
              {displayTag}
              {filterType === "love" && (
                <Heart className="h-3 w-3 ml-1 fill-rose-600 text-rose-600 dark:fill-rose-400 dark:text-rose-400 flex-shrink-0" />
              )}
              {filterType !== "love" && isHidden && <Ban className="h-3 w-3 ml-1 text-gray-900 dark:text-gray-100 flex-shrink-0" />}
              {disputeCount > 0 && !isLocked && <AlertCircle className="h-3 w-3 ml-1 text-orange-500 flex-shrink-0" />}
              {isLocked && <Lock className="h-3 w-3 ml-1 text-blue-500 flex-shrink-0" />}
            </Badge>
            {showMinus && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onNegativeClick?.()
                }}
                className="opacity-0 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100 transition-opacity px-1 hover:text-destructive"
                title={t("addAsNegativeTag")}
              >
                <Minus className="h-3 w-3 text-destructive" />
              </button>
            )}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-44 md:w-56">
          <ContextMenuItem onClick={onClick}>
            <Plus className="h-4 w-4 mr-2" />
            {t("addToCurrentSearch")}
          </ContextMenuItem>
          {onNegativeClick && (
            <ContextMenuItem onClick={onNegativeClick}>
              <Minus className="h-4 w-4 mr-2" />
              {t("addAsNegativeTag2")}
            </ContextMenuItem>
          )}
          <ContextMenuSeparator />
          <ContextMenuItem onClick={handleOpenInNewSetCallback}>
            <ExternalLink className="h-4 w-4 mr-2" />
            {t("openInNewSet")}
          </ContextMenuItem>
          <ContextMenuItem onClick={handleOpenInBackgroundSetCallback}>
            <Archive className="h-4 w-4 mr-2" />
            {t("openInBackgroundSet")}
          </ContextMenuItem>
          {tagSets.length > 0 && (
            <ContextMenuSub>
              <ContextMenuSubTrigger>
                <ListPlus className="h-4 w-4 mr-2" />
                {t("addToSet")}
              </ContextMenuSubTrigger>
              <ContextMenuSubContent className="w-48 md:w-56 max-h-72 overflow-y-auto">
                {tagSets.map((set) => {
                  const already = set.tags.includes(tag)
                  return (
                    <ContextMenuItem
                      key={set.id}
                      disabled={already}
                      onClick={() => addToSet(set.id, set.name, set.tags, set.negativeTags)}
                    >
                      {already ? <Check className="h-4 w-4 mr-2" /> : <span className="w-4 mr-2" />}
                      <span className="truncate">{set.name || set.tags.join(" ")}</span>
                    </ContextMenuItem>
                  )
                })}
              </ContextMenuSubContent>
            </ContextMenuSub>
          )}
          <ContextMenuSeparator />
          <ContextMenuItem onClick={handleLoveTag}>
            <Heart className={`h-4 w-4 mr-2 ${filterType === "love" ? "fill-rose-500 text-rose-500" : ""}`} />
            {filterType === "love" ? t("removeFromLoved") : t("loveThisTag")}
          </ContextMenuItem>
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <EyeOff className={`h-4 w-4 mr-2 ${isHidden ? "text-black dark:text-white" : ""}`} />
              {t("hideThisTag")}
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="w-48 md:w-56">
              <ContextMenuItem onClick={toggleHiddenAlone}>
                {hiddenAlone ? <Check className="h-4 w-4 mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
                {hiddenAlone ? t("unhideThisTag") : t("justThisTag")}
              </ContextMenuItem>
              {blacklistGroups.length > 0 && <ContextMenuSeparator />}
              {blacklistGroups.map((group) => {
                const inGroup = group.tags.includes(cleanTag)
                return (
                  <ContextMenuItem
                    key={group.id}
                    onClick={() => toggleInGroup(group.id, inGroup, group.name)}
                  >
                    {inGroup ? <Check className="h-4 w-4 mr-2" /> : <span className="w-4 mr-2" />}
                    <span className="truncate">{group.name}</span>
                  </ContextMenuItem>
                )
              })}
              <ContextMenuSeparator />
              <ContextMenuItem onClick={() => setShowNewGroupDialog(true)}>
                <FolderPlus className="h-4 w-4 mr-2" />
                {t("newGroup")}
              </ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>
          {suggestionsOn && isImportedTag && onDispute && !isLocked && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => {
                  setShowDisputeDialog(true)
                }}
              >
                <Flag className="h-4 w-4 mr-2" />
                {t("disputeThisTag")}
              </ContextMenuItem>
            </>
          )}
          {isLocked && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem disabled>
                <Lock className="h-4 w-4 mr-2" />
                {t("tagIsLocked")}
              </ContextMenuItem>
            </>
          )}
          {suggestionsOn && (
            <>
              <ContextMenuSeparator />
              <ContextMenuItem
                onClick={() => handleTrustVote(true)}
                disabled={!postId}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                {t("accurate")}
              </ContextMenuItem>
              <ContextMenuItem
                onClick={() => handleTrustVote(false)}
                disabled={!postId}
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                {t("inaccurate")}
              </ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>

      <Dialog open={showNewGroupDialog} onOpenChange={setShowNewGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("newGroupTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t("newGroupHint", { name: displayTag })}</p>
          <Input
            id="new-hide-group-name"
            placeholder={t("newGroupPlaceholder")}
            value={newGroupName}
            maxLength={40}
            autoFocus
            onChange={(e) => setNewGroupName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateGroup()
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewGroupDialog(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={handleCreateGroup} disabled={!newGroupName.trim()}>
              {t("createAndHide")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDisputeDialog} onOpenChange={setShowDisputeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("disputeTagName", { name: displayTag })}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("explainWhyThisTagShouldBeRemovedFromThis")}
            </p>
            <Input
              placeholder={t("enterDisputeReason")}
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              maxLength={100}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleDispute()
              }}
            />
            <p className="text-xs text-muted-foreground">{t("countOf100Characters", { count: disputeReason.length })}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisputeDialog(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={handleDispute} disabled={disputeReason.trim().length < 10}>
              {t("submitDispute")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
})
